import React, { useCallback, useEffect, useState } from 'react';
import { Button, Dropdown, MdSpace, NumberField, Section, TextField } from 'm2m.components';
import {
  BloqueioDocumentType,
  BloqueioRequest,
  BloqueioTipo,
  FuncaoParticipante,
  InvestidorResponse,
  ParticipanteResponse,
  PosicaoResponse,
  RegistroStatus,
} from 'common/genericTypes';
import { useDispatch, useSelector } from 'react-redux';
import { addCpfCnpjMask } from 'utils/mask';
import FinishedSign from 'components/finishedSign';
import { Grid, Box } from '@material-ui/core';
import { PlaylistAdd } from '@material-ui/icons';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { RootState } from 'store';
import { getQuantityType } from 'utils/instrumentoFinanceiroUtils';
import { formatNumber } from 'utils/number';
import BloqueiosGarantiaEmLote from 'components/bloqueiosGarantiaEmLote/bloqueiosGarantiaEmLote';

const CadastroBloqueioPorGarantia: React.FC = () => {
  const [custodiantes, setCustodiantes] = useState<Array<ParticipanteResponse>>([]);
  const [investidoresGarantidores, setInvestidoresGarantidores] = useState<Array<InvestidorResponse>>([]);
  const [posicoesInvestidores, setPosicoesInvestidores] = useState<Array<PosicaoResponse>>([]);
  const [posicoesAtivos, setPosicoesAtivos] = useState<Array<PosicaoResponse>>([]);
  const [quantidadeDisponivel, setQuantidadeDisponivel] = useState<number>(0);
  const [investidoresGarantidos, setInvestidoresGarantidos] = useState<Array<InvestidorResponse>>([]);

  const [custodianteGarantidorId, setCustodianteGarantidorId] = useState<string>('');
  const [investidorGarantidorId, setInvestidorGarantidorId] = useState<string>('');
  const [contaDepositoGarantidor, setContaDepositoGarantidor] = useState<string>('');
  const [instrumentoGuid, setInstrumentoGuid] = useState<string>('');
  const [quantidade, setQuantidade] = useState<number>(0);
  const [custodianteGarantidoId, setCustodianteGarantidoId] = useState<string>('');
  const [investidorGarantidoId, setInvestidorGarantidoId] = useState<string>('');
  const [contaDepositoGarantido, setContaDepositoGarantido] = useState<string>('');
  const [justificativa, setJustificativa] = useState<string>('');
  const [anexos, setAnexos] = useState<Array<Attachment>>([]);
  const [informacoesDeBloqueioFulfilled, setInformacoesDeBloqueioFulfilled] = useState<boolean>(false);
  const [formFulfilled, setFormFulfilled] = useState<boolean>(false);
  const [finished, setFinished] = useState(false);
  const [mostrarCadastroEmLote, setMostrarCadastroEmLote] = useState<boolean>(false);

  const dispatch = useDispatch();
  const { getParticipantes: getParticipantesApi } = useSelector((state: RootState) => state.participanteApi);
  const {
    getInvestidores: getInvestidoresApi,
    getPosicoes: getPosicoesApi,
    postBloqueio,
    putUploadBloqueioDocs,
  } = useSelector((state: RootState) => state.investidorApi);

  const getParticipantes = useCallback(
    async (categoria: FuncaoParticipante): Promise<Array<ParticipanteResponse>> => {
      return await getParticipantesApi(
        dispatch,
        { funcao: categoria, status: RegistroStatus.Aprovado, suspenso: false },
        true
      );
    },
    [dispatch, getParticipantesApi]
  );

  const getPosicoes = useCallback(
    async (custodianteId: string, investidorId: string | undefined): Promise<Array<PosicaoResponse>> => {
      return await getPosicoesApi(dispatch, { custodianteId, investidorId }, true);
    },
    [dispatch, getPosicoesApi]
  );

  const getInvestidores = useCallback(
    async (custodianteId: string): Promise<Array<InvestidorResponse>> => {
      return await getInvestidoresApi(dispatch, { participanteId: custodianteId }, true);
    },
    [dispatch, getInvestidoresApi]
  );

  const handleSubmit = async () => {
    const bloqueioRequest: BloqueioRequest = {
      bloqueioTipo: BloqueioTipo.Garantia,
      custodianteId: custodianteGarantidorId,
      investidorId: investidorGarantidorId,
      instrumentoFinanceiroId: instrumentoGuid,
      quantidade,
      justificativa,
      custodianteGarantidoId,
      investidorGarantidoId,
    };
    try {
      const response = await postBloqueio(dispatch, bloqueioRequest, true);
      if (response?.id && anexos?.length) {
        await putUploadBloqueioDocs(
          dispatch,
          response.id,
          BloqueioDocumentType.Bloqueio,
          anexos.filter((_) => _.file).map((_) => _.file!),
          true
        );
      }
      setFinished(true);
    } catch (error) {}
  };

  const handleRemoveClick = (id: string) => {
    const updatedAnexos = anexos.filter((anexo) => anexo.id !== id);
    setAnexos(updatedAnexos);
  };

  const handleAnexosChange = (atts: Attachment[]) => {
    const updatedAnexos = anexos.concat(atts);
    setAnexos(updatedAnexos);
  };

  useEffect(() => {
    getParticipantes(FuncaoParticipante.Custodiante).then(setCustodiantes).catch();
  }, [dispatch, getParticipantes]);

  useEffect(() => {
    if (!custodianteGarantidorId) {
      setInvestidoresGarantidores([]);
      setPosicoesInvestidores([]);
      setPosicoesAtivos([]);
      setInstrumentoGuid('');
    } else {
      getInvestidores(custodianteGarantidorId)
        .then(setInvestidoresGarantidores)
        .catch((error) => {});
      getPosicoes(custodianteGarantidorId, undefined)
        .then((result) => setPosicoesInvestidores(result.filter((_) => _.saldoDisponivel > 0)))
        .catch((error) => {});
    }
  }, [dispatch, getInvestidores, getPosicoes, custodianteGarantidorId]);

  useEffect(() => {
    if (!custodianteGarantidorId) {
      setPosicoesInvestidores([]);
      setPosicoesAtivos([]);
      setInstrumentoGuid('');
    } else {
      getPosicoes(custodianteGarantidorId, investidorGarantidorId)
        .then((result) => setPosicoesAtivos(result.filter((_) => _.saldoDisponivel > 0)))
        .catch(() => {});
    }
  }, [dispatch, getPosicoes, custodianteGarantidorId, investidorGarantidorId]);

  useEffect(() => {
    const posicoes = posicoesInvestidores.filter((_) => _.investidor.id === investidorGarantidorId);
    setPosicoesAtivos(posicoes);

    const investidor = investidoresGarantidores.find((_) => _.id === investidorGarantidorId);
    setContaDepositoGarantidor(investidor?.contaDeposito ?? '');
  }, [investidorGarantidorId, investidoresGarantidores, posicoesInvestidores]);

  useEffect(() => {
    setQuantidadeDisponivel(
      posicoesAtivos.find((_) => _.instrumentoFinanceiro.instrumentoFinanceiroGuid === instrumentoGuid)?.saldoDisponivel ?? 0
    );
  }, [instrumentoGuid, posicoesAtivos]);

  useEffect(() => {
    if (!custodianteGarantidoId) {
      setInvestidoresGarantidos([]);
      setContaDepositoGarantido('');
    } else {
      getInvestidores(custodianteGarantidoId)
        .then((result) => {
          setInvestidoresGarantidos(result.filter((_) => _.id !== investidorGarantidorId));
        })
        .catch(() => {});
    }
  }, [dispatch, getInvestidores, custodianteGarantidoId, investidorGarantidorId]);

  useEffect(() => {
    const investidor = investidoresGarantidores.find((_) => _.id === investidorGarantidorId);
    setContaDepositoGarantidor(investidor?.contaDeposito ?? '');
  }, [investidorGarantidorId, investidoresGarantidores]);

  useEffect(() => {
    const investidor = investidoresGarantidos.find((_) => _.id === investidorGarantidoId);
    setContaDepositoGarantido(investidor?.contaDeposito ?? '');
  }, [investidorGarantidoId, investidoresGarantidos]);

  useEffect(() => {
    setFormFulfilled(informacoesDeBloqueioFulfilled && (!!anexos?.length || !!justificativa));
  }, [informacoesDeBloqueioFulfilled, anexos, justificativa]);

  return (
    <>
      {mostrarCadastroEmLote ? (
        <BloqueiosGarantiaEmLote 
          onVoltar={() => setMostrarCadastroEmLote(false)}
        />
      ) : finished ? (
        <FinishedSign
          description={'Cadastro de bloqueio concluído com sucesso'}
          redirectRoute={'/gerenciar/investidores'}
        />
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            sx={{ mb: 1, mt: 1 }}
          >
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PlaylistAdd />}
              onClick={() => setMostrarCadastroEmLote(true)}
              size="small"
            >
              Cadastro em Lote
            </Button>
          </Box>
          <Section title={'Informações do Bloqueio'} defaultGrid isFulfilled={setInformacoesDeBloqueioFulfilled}>
            <Dropdown
              label={'Custodiante Garantidor'}
              value={custodianteGarantidorId}
              options={custodiantes.map((_) => ({ value: _.id, description: _.razaoSocial }))}
              onChange={setCustodianteGarantidorId}
              required
            />
            <Dropdown
              label={'CPF/CNPJ do Investidor Garantidor'}
              value={investidorGarantidorId}
              options={Array.from(new Set(posicoesInvestidores.map((_) => _.investidor.id))).map((id) => ({
                value: id,
                description: addCpfCnpjMask(
                  posicoesInvestidores.find((_) => _.investidor.id === id)?.investidor.cpfCnpj ?? ''
                ),
              }))}
              onChange={setInvestidorGarantidorId}
              required
            />
            <TextField
              label={'Conta depósito do Garantidor'}
              value={contaDepositoGarantidor}
              onChange={() => {}}
              disabled
            />
            <Dropdown
              label={'Código do Ativo'}
              value={instrumentoGuid}
              options={posicoesAtivos.map((_) => ({
                value: _.instrumentoFinanceiro.instrumentoFinanceiroGuid,
                description: _.instrumentoFinanceiro.codigo,
              }))}
              onChange={(value) => setInstrumentoGuid(value)}
              required
            />
            <NumberField
              label={'Quantidade'}
              value={quantidade}
              numberType={getQuantityType(posicoesAtivos.find((_) => _.instrumentoFinanceiro.instrumentoFinanceiroGuid === instrumentoGuid)?.instrumentoFinanceiro.tipoInstrumentoFinanceiro)}
              helperText={`Quantidade disponível: ${formatNumber(quantidadeDisponivel, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}`}
              onChange={(value) => setQuantidade(value as number)}
              required
            />
            <Dropdown
              label={'Custodiante Garantido'}
              value={custodianteGarantidoId}
              options={custodiantes.map((_) => ({ value: _.id, description: _.razaoSocial }))}
              onChange={setCustodianteGarantidoId}
            />
            <Dropdown
              label={'CPF/CNPJ do Investidor Garantido'}
              value={investidorGarantidoId}
              options={investidoresGarantidos.map((_) => ({ value: _.id, description: addCpfCnpjMask(_.cpfCnpj) }))}
              onChange={setInvestidorGarantidoId}
            />
            <TextField
              label={'Conta depósito do Garantido'}
              value={contaDepositoGarantido}
              onChange={() => {}}
              disabled
            />
            <TextField
              label="Justificativa"
              value={justificativa}
              rows={5}
              onChange={setJustificativa}
              multiline
              fullWidth
              required
            />
          </Section>
          <Section title={'Anexos'}>
            <UploadDocs onChange={handleAnexosChange} onDelete={handleRemoveClick} multipleFiles={true} />
          </Section>
          <Grid container justify="center">
            <Grid item>
              <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!formFulfilled}>
                {'Comandar Bloqueio'}
              </Button>
              <MdSpace />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default CadastroBloqueioPorGarantia;
